import { useState, type ReactNode } from "react";
import { ExternalLink, History, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { ORIGIN_LABELS, nfDanfeAccessKey, referenceLabel, type CostHistoryView } from "@/lib/costHistoryFormat";
import { viewSupplierInvoicePdf } from "@/lib/adminSupplierInvoicesApi";

export type { CostHistoryView } from "@/lib/costHistoryFormat";

const brl = new Intl.NumberFormat("pt-BR", { style: "currency", currency: "BRL" });

/** Campos cujo valor registrado é monetário (formatados em BRL ao exibir anterior → novo). */
const MONETARY_FIELDS = new Set<string>([
  "Custo da matéria-prima",
  "Valor operacional",
  "Custo",
]);

function displayHistoryValue(label: string, value: string): string {
  if (value === "—" || value.trim() === "") return "—"; // ausência (distinta de zero)
  if (MONETARY_FIELDS.has(label)) {
    const n = Number(value.replace(",", "."));
    return Number.isFinite(n) ? brl.format(n) : value;
  }
  return value;
}

/** Pareia as linhas "Campo: valor" de oldValues/newValues → "Campo: anterior → novo". */
function pairHistory(oldValues: string, newValues: string): { label: string; from: string; to: string }[] {
  const split = (s: string) =>
    (s ?? "").split("\n").filter((l) => l.trim() !== "").map((line) => {
      const i = line.indexOf(":");
      return i < 0
        ? { label: line.trim(), value: "" }
        : { label: line.slice(0, i).trim(), value: line.slice(i + 1).trim() };
    });
  const olds = split(oldValues);
  const news = split(newValues);
  const n = Math.max(olds.length, news.length);
  const rows: { label: string; from: string; to: string }[] = [];
  for (let i = 0; i < n; i++) {
    rows.push({ label: news[i]?.label ?? olds[i]?.label ?? "", from: olds[i]?.value ?? "—", to: news[i]?.value ?? "—" });
  }
  return rows;
}

/**
 * Ícone de relógio que abre o histórico de UM componente de custo. O `load` devolve apenas as alterações
 * daquele componente (o chamador já filtra por campo), para que uma edição do operacional nunca apareça como
 * mudança da matéria-prima. Mostra data/hora, responsável, unidade, anterior → novo, origem e a referência
 * (NF/XLSX) quando disponível. Para custos herdados, use `subtitle` para nomear a matéria-prima/material de
 * origem.
 */
export function CostComponentHistory({
  title,
  subtitle,
  load,
  emptyText = "Sem alterações registradas.",
}: {
  title: string;
  subtitle?: ReactNode;
  load: () => Promise<CostHistoryView[]>;
  emptyText?: string;
}) {
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [entries, setEntries] = useState<CostHistoryView[] | null>(null);
  const [openingDanfe, setOpeningDanfe] = useState<string | null>(null);

  // Abre o "DANFE gerado do XML" da NF que originou o custo, em nova aba (preserva o histórico aberto).
  // A permissão de acesso ao documento é enforçada no backend; indisponibilidade (sem XML, sem permissão)
  // vira mensagem clara, sem derrubar o diálogo.
  const openDanfe = (accessKey: string) => {
    setOpeningDanfe(accessKey);
    viewSupplierInvoicePdf(accessKey)
      .catch((e) =>
        toast.error(e instanceof Error ? e.message : "Não foi possível abrir o DANFE desta NF."),
      )
      .finally(() => setOpeningDanfe(null));
  };

  const openDialog = () => {
    setOpen(true);
    if (entries == null && !loading) {
      setLoading(true);
      load()
        .then(setEntries)
        .catch((e) => {
          toast.error(e instanceof Error ? e.message : "Não foi possível carregar o histórico");
          setOpen(false);
        })
        .finally(() => setLoading(false));
    }
  };

  return (
    <>
      <Button
        type="button"
        variant="ghost"
        size="icon"
        className="h-7 w-7 text-muted-foreground"
        title={`Histórico — ${title}`}
        aria-label={`Histórico — ${title}`}
        onClick={openDialog}
      >
        <History className="h-4 w-4" />
      </Button>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-h-[80vh] max-w-lg overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Histórico — {title}</DialogTitle>
          </DialogHeader>
          {subtitle && <p className="text-xs text-muted-foreground">{subtitle}</p>}

          {loading && (
            <div className="py-6 text-center text-xs text-muted-foreground">
              <Loader2 className="mx-auto mb-1 h-4 w-4 animate-spin" /> Carregando…
            </div>
          )}
          {!loading && (entries?.length ?? 0) === 0 && (
            <p className="py-6 text-center text-xs text-muted-foreground">{emptyText}</p>
          )}
          {!loading && (entries ?? []).length > 0 && (
            <div className="space-y-2">
              {(entries ?? []).map((h) => (
                <div key={h.id} className="rounded-md border border-border bg-background p-2 text-xs">
                  <div className="flex flex-wrap items-center justify-between gap-1">
                    <span className="font-medium">{new Date(h.changedAt).toLocaleString("pt-BR")}</span>
                    <span className="rounded bg-secondary px-1.5 py-0.5 text-[10px] uppercase tracking-wide text-muted-foreground">
                      {ORIGIN_LABELS[h.origin] ?? h.origin ?? "Manual"}
                    </span>
                  </div>
                  <div className="mt-1 text-muted-foreground">por {h.changedBy}</div>
                  {h.unitName && (
                    <div className="mt-0.5 text-muted-foreground">Unidade de referência: {h.unitName}</div>
                  )}
                  <ul className="mt-1 space-y-0.5">
                    {pairHistory(h.oldValues, h.newValues).map((r, i) => (
                      <li key={i}>
                        <span className="font-medium">{r.label}:</span>{" "}
                        {displayHistoryValue(r.label, r.from)}{" "}
                        <span className="text-muted-foreground">→</span>{" "}
                        {displayHistoryValue(r.label, r.to)}
                      </li>
                    ))}
                  </ul>
                  <div className="mt-1 text-muted-foreground">
                    Referência:{" "}
                    {(() => {
                      const accessKey = nfDanfeAccessKey(h);
                      if (!accessKey) return referenceLabel(h);
                      return (
                        <button
                          type="button"
                          className="inline-flex items-center gap-1 font-medium text-primary underline underline-offset-2 hover:opacity-80 disabled:opacity-60"
                          onClick={() => openDanfe(accessKey)}
                          disabled={openingDanfe === accessKey}
                          title="Abrir o DANFE gerado do XML em nova aba"
                        >
                          {referenceLabel(h)}
                          {openingDanfe === accessKey ? (
                            <Loader2 className="h-3 w-3 animate-spin" />
                          ) : (
                            <ExternalLink className="h-3 w-3" />
                          )}
                        </button>
                      );
                    })()}
                  </div>
                </div>
              ))}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </>
  );
}
